import type { PostHogConfig } from "posthog-js";
import { sampleByDistinctId } from "posthog-js/lib/src/customizations";
import { blockToolbarScript, stripToolbarPayloadFromUrl } from "@/utils/posthog_toolbar_guard";

// Page views/leaves must stay at 100% per PostHog guidance, identify/alias events
// keep the anon→user link intact, and $snapshot is governed by replay's own sampling.
const UNSAMPLED_EVENTS = new Set([
  "$pageview",
  "$pageleave",
  "$identify",
  "$create_alias",
  "$groupidentify",
  "$snapshot",
]);

const AUTH_PAGE_ALLOWED_EVENTS = new Set(["social_login_clicked"]);

export function getPostHogOptions(): Partial<PostHogConfig> {
  const sampler = sampleByDistinctId(window.ENV.POSTHOG_SAMPLE_RATE);
  return {
    api_host: `https://${window.ENV.POSTHOG_HOST}`,
    autocapture: window.ENV.POSTHOG_AUTOCAPTURE || false,
    capture_pageview: "history_change", // Tracks initial load + SPA navigations; pathname-change guard prevents replaceState overcounting
    before_send: (event) => {
      if (
        (event?.properties?.$pathname === "/users/sign_in" ||
          event?.properties?.$pathname === "/users/new") &&
        !AUTH_PAGE_ALLOWED_EVENTS.has(event?.event)
      ) {
        return null;
      }
      if (event?.properties) {
        for (const key of ["$current_url", "$referrer"] as const) {
          const value = event.properties[key];
          if (typeof value === "string") {
            event.properties[key] = stripToolbarPayloadFromUrl(value);
          }
        }
      }
      if (event?.event === "$pageview" && event.properties?.$pathname === "/jobs/search") {
        const url = event.properties.$current_url;
        if (typeof url === "string") {
          const parsed = new URL(url);
          parsed.search = "";
          event.properties.$current_url = parsed.toString();
        }
      }
      if (event && !UNSAMPLED_EVENTS.has(event.event)) {
        return sampler(event);
      }
      return event;
    },
    // Privacy & compliance — do not disable these
    respect_dnt: true,
    disable_session_recording: window.ENV.DISABLE_POSTHOG_SESSION_RECORDING ?? true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      blockSelector: "img",
    },
    // Flipper handles feature flags — do not enable PostHog flags
    advanced_disable_feature_flags: true,
    prepare_external_dependency_script: blockToolbarScript,
  };
}

export function isPostHogEnabled(): boolean {
  return window.ENV.POSTHOG_ENABLED || false;
}
